import {Component, OnInit, OnDestroy, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ReportsService} from '../../data/reports.service';
import {QbEstimateDTO} from '../../data/qb-estimate-dto';
import {trackEntityById} from 'projects/core/src/public-api';
import {
  ProjectWindowComponent
} from 'projects/desktop/src/app/modules/project/view/project-window/project-window.component';
import {DeviceDetectorService} from 'ngx-device-detector';
import {ProjectInfoComponent} from 'projects/mobile/src/app/modules/project/view/project-info/project-info.component';
import {SaleStat} from '../../data/sale-stat';
import {ProjectService} from '../../../project-misc/data/project/project.service';
import {environment} from 'projects/desktop/src/environments/environment';
import {EstimateEditorDialogComponent} from '../estimate-editor-dialog/estimate-editor-dialog.component';
import {ConfirmationService} from 'primeng/api';
import {MiscSnackBarService} from 'projects/core/src/lib/misc/view/snackbar/misc-snackbar.service';
import {forkJoin, Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {WsService} from '../../../data/ws.service';
import {WsUpdateResponce} from '../../../data/ws-update-responce';
import {MissingEstimateProject} from '../../data/missing-estimate-project';

@Component({
  selector: 'app-schedule-estimates-dialog',
  templateUrl: './schedule-estimates-dialog.component.html',
  styleUrls: ['./schedule-estimates-dialog.component.scss'],
  providers: [DeviceDetectorService]
})
export class ScheduleEstimatesDialogComponent implements OnInit, OnDestroy {
  estimates: QbEstimateDTO[] = [];
  copyList: QbEstimateDTO[] = [];
  projectsWithoutEstimates: MissingEstimateProject[] = [];

  sortMap = new Map();
  lastSortLabel: string;

  contentLoading: boolean;

  trackEntityById = trackEntityById;

  userId: number;
  userName: string;
  startDate: string;
  finishDate: string;

  totalInfo: SaleStat;

  private wsSub: Subscription;

  constructor(
    protected dialogRef: MatDialogRef<ScheduleEstimatesDialogComponent>,
    protected deviceDetectorService: DeviceDetectorService,
    protected dialog: MatDialog,
    protected projectService: ProjectService,
    protected reportsService: ReportsService,
    protected confirmationService: ConfirmationService,
    protected miscSnackBarService: MiscSnackBarService,
    protected wsService: WsService,
    @Inject(MAT_DIALOG_DATA) private data: {
      userId: number,
      userName: string,
      startDate: string,
      finishDate: string,
      info: SaleStat,
      fromSchedule: boolean
    }) {
    this.userId = data.userId;
    this.userName = data.userName;
    this.startDate = data.startDate;
    this.finishDate = data.finishDate;

    this.totalInfo = data.info ? Object.assign({}, data.info) : new SaleStat();
  }

  ngOnInit(): void {
    this.wsSubscribe();
    this.loadData();
  }

  wsSubscribe(): void {
    if (this.wsSub) {
      return;
    }
    const start = new Date(this.data.startDate).getTime();
    const end = new Date(this.data.finishDate).getTime();
    const isInRange = (txnDate: any) => {
      if (!txnDate) {
        return false;
      }
      const t = new Date(txnDate).getTime();
      return t >= start && t <= end;
    };
    this.wsSub = this.wsService.listenUntilDestroyed<WsUpdateResponce<QbEstimateDTO>>('/topic/update/model.qb.QbEstimate', this)
      .pipe(
        filter(({entity, oldEntity}) =>
          isInRange(entity?.txnDate) || (!!oldEntity && isInRange(oldEntity?.txnDate))
        )
      )
      .subscribe(() => {
        this.loadData();
      });
  }

  ngOnDestroy(): void {
    if (this.wsSub) {
      this.wsSub.unsubscribe();
      this.wsSub = null;
    }
  }

  loadData() {
    this.estimates = [];
    this.copyList = [];
    this.projectsWithoutEstimates = [];

    this.nullifyMap();

    this.contentLoading = true;

    forkJoin({
      estimates: this.reportsService.getEstimates(this.data.startDate, this.data.finishDate),
      missingProjects: this.reportsService.getMissingEstimateProjects(this.data.startDate, this.data.finishDate)
    }).subscribe(({estimates, missingProjects}) => {
      this.estimates = this.data.fromSchedule ? estimates : estimates.filter(est => est.salesUserId === this.data.userId);
      this.copyList = [...this.estimates];

      const projectIdsWithEstimates = new Set(this.estimates.map(est => est.projectId));
      this.projectsWithoutEstimates = missingProjects.filter(p => !projectIdsWithEstimates.has(p.id));

      this.countTotals();
      this.contentLoading = false;
      this.sortByLabel('txnDate');
    }, () => {
      this.contentLoading = false;
    });
  }

  countTotals() {
    this.totalInfo.totalAmt = 0;
    this.estimates.forEach(est => {
      this.totalInfo.totalAmt += est.amount || 0;
    });
  }

  nullifyMap() {
    this.sortMap.set('txnDate', 0);
    this.sortMap.set('projectNumber', 0);
    this.sortMap.set('amount', 0);
    this.sortMap.set('projectName', 0);
    this.sortMap.set('txnStatus', 0);
  }

  countMap(param: string) {
    let num = this.sortMap.get(param);
    if (num === 1) {
      num++;
    } else if (num === 0) {
      this.nullifyMap();
      num++;
    } else if (num === 2) {
      num = 0;
    }
    this.sortMap.set(param, num);
  }

  sortByLabel(param: string) {
    if (!this.contentLoading) {
      this.lastSortLabel = param;

      this.countMap(param);
      let numm = 1;
      if (this.sortMap.get(param) === 2) {
        numm = -1;
      }
      if (this.sortMap.get(param) === 0) {
        this.estimates = [...this.copyList];
        return;
      }

      this.estimates.sort((a, b) => {
        if (param === 'txnDate') {
          const aTime = a.txnDate ? new Date(a.txnDate).getTime() : 0;
          const bTime = b.txnDate ? new Date(b.txnDate).getTime() : 0;
          return (aTime - bTime) * numm;
        }
        if (param === 'txnStatus' || param === 'projectNumber') {
          let aString = a[param] || '';
          let bString = b[param] || '';
          if (aString === '') { aString = 'z'; }
          if (bString === '') { bString = 'z'; }
          if (aString < bString) { return -1 * numm; }
          if (aString > bString) { return 1 * numm; }
          return 0;
        }
        if (param === 'projectName') {
          const aString = a.projectName || 'z';
          const bString = b.projectName || 'z';
          if (aString < bString) { return -1 * numm; }
          if (aString > bString) { return 1 * numm; }
          return 0;
        }
        return (b[param] - a[param]) * numm;
      });
    }
  }

  openProject(projectId: number) {
    let projectWindow = ProjectWindowComponent as any;
    let panelClass = 'project-window-dialog';

    if (this.deviceDetectorService.isMobile() || this.deviceDetectorService.isTablet()) {
      projectWindow = ProjectInfoComponent;
      panelClass = 'project-info-dialog';
    }

    this.dialog.open(projectWindow, {
      data: {projectNumber: projectId},
      panelClass
    });
  }

  openEstimateEditor(estimate?: QbEstimateDTO) {
    this.dialog.open(EstimateEditorDialogComponent, {
      data: {
        estimate,
        startDate: this.startDate,
        finishDate: this.finishDate
      },
      panelClass: 'estimate-editor-dialog',
      disableClose: false
    }).afterClosed().subscribe(result => {
      if (result) {
        this.loadData();
      }
    });
  }

  deleteEstimate(estimate: QbEstimateDTO) {
    if (!estimate.createdManually) {
      return;
    }
    this.confirmationService.confirm({
      header: 'Delete Estimate?',
      message: `Delete estimate for ${estimate.projectName}?`,
      dismissableMask: true,
      icon: 'pi pi-trash',
      accept: () => {
        this.reportsService.deleteEstimate(estimate.id).subscribe(() => {
          this.loadData();
        });
      }
    });
  }

  openInQb(estimate: QbEstimateDTO) {
    let baseLink = `https://app.qbo.intuit.com/app`;
    if (environment.dev) {
      baseLink = `https://app.sandbox.qbo.intuit.com/app`;
    }
    window.open(`${baseLink}/estimate?txnId=${estimate.qbTxnId}`, '_blank');
  }

  getOriginalAmount(estimate: QbEstimateDTO): string {
    if (!estimate.parentId || !estimate.percentage || !estimate.originalAmount) {
      return '';
    }
    return estimate.originalAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
  }

  openEstimateEditorForProject(project: MissingEstimateProject) {
    this.dialog.open(EstimateEditorDialogComponent, {
      data: {
        projectId: project.id,
        defaultDate: project.date,
        startDate: this.startDate,
        finishDate: this.finishDate
      },
      panelClass: 'estimate-editor-dialog',
      disableClose: false
    }).afterClosed().subscribe(result => {
      if (result) {
        this.loadData();
      }
    });
  }

  trackProjectById(index: number, project: MissingEstimateProject): number {
    return project.id;
  }

  close() {
    this.dialogRef.close(true);
  }
}

